import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { mapSkillStatus } from '../../businessLogicService/mapSkillStatus';
import { SkillResource } from '../SkillResource/SkillResource';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { PreloadedQuery, useLazyLoadQuery, useQueryLoader } from 'react-relay';
import { selectUser } from '../../redux/reducers/userSlice';
import { PreviousAttempts } from '../../models/PreviousAttempts';
import { getStudentSingleSkillQuery } from '../../graphQLOperations/StudentSingleSkill';
import { TargetGroup } from '../../models/TargetGroup';
import { selectedTargetGroup, setTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { StudentSkillStates } from '../../models/StudentSkillState';
import {
  PreviousAndNextSSS,
  selectedStudentSkillStateAfterTakeAssessment,
} from '../../redux/reducers/studentSkillAfterTakeAssessmentSlice';
import { inferredOrDiscrepancy } from '../../businessLogicService/inferredOrDiscrepancySubstate';
import { SkillSubStates } from '../../models/SkillState';
import { AttemptInformation } from '../../models/AttemptInformation';
import { BreadCrumb } from 'primereact/breadcrumb';
import { parseSkillResourceStepSlug, SKILL_RESOURCE_STEP_PARAM, SkillResourceSteps } from '../../models/Timeline';
import { House } from 'lucide-react';
import { StudentSingleSkillQuery } from '../../graphQLOperations/__generated__/StudentSingleSkillQuery.graphql';
import { useSkillExitSlipState } from '../../custom-hooks/useSkillExitSlipState';
import SkillExitSlipReportQuery from './SkillExitSlipReportQuery';
import { SkillsPresignedURLsQuery } from '../../graphQLOperations/__generated__/SkillsPresignedURLsQuery.graphql';
import { getPresignedURLsForSkill } from '../../graphQLOperations/Skills';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { useCooldown } from '../../contexts/RetakeCountdownContext';

interface Props {
  forceRefetch: string;
}
export interface PreviousStatus {
  id: string;
  date: string;
  score: number;
  maxScore?: number | null;
}

export interface CurrentStatus {
  classname: string;
  status: string | undefined;
}

export const StudentSingleSkill: FC<Props> = ({ forceRefetch }) => {
  const { skillCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // A `?step=` deep link, e.g. from the exit-ticket result card on the dashboard. Null for a plain
  // /skill/:skillCode visit, which is every other way into this page.
  const requestedStep = parseSkillResourceStepSlug(searchParams.get(SKILL_RESOURCE_STEP_PARAM));
  const dispatch = useAppDispatch();
  const cooldownCtx = useCooldown();
  const userRedux = useAppSelector(selectUser);
  const targetGroupRedux = useAppSelector(selectedTargetGroup);
  const sSAfterTakeAssessmentRedux = useAppSelector(selectedStudentSkillStateAfterTakeAssessment);
  const [assessmentInProgress, setAssessmentInProgress] = useState(false);
  const [attemptInformation, setAttemptInformation] = useState<AttemptInformation | null>(null);
  const [previousAttempts, setPreviousAttempts] = useState<PreviousAttempts[]>([]);
  const [sssAfterTakeAssessment, setSSSAfterTakeAssessment] = useState<PreviousAndNextSSS>();
  const [currentStep, setCurrentStep] = useState<SkillResourceSteps | null>(null);
  const [skipNewActivityId, setSkipNewActivityId] = useState<boolean>(true);
  const [currentStatus, setCurrentStatus] = useState<CurrentStatus | null>(null);
  const [previousStatus, setPreviousStatus] = useState<PreviousStatus[]>([]);

  const {
    skillStatesByStudentId,
    currentTargetGroupByStudentId,
    skillAssessmentsByStudentId,
    allUserActions,
    latestCompletedStudentSkillDiagnostic,
  } = useLazyLoadQuery<StudentSingleSkillQuery>(
    getStudentSingleSkillQuery,
    {
      studentId: `StudentNode:${userRedux?.id}`,
      skillCode: skillCode || '',
      skillAssessmentsByStudentFilter: { skillCode: skillCode || '' },
      allUserActionsFilter: {
        userId: `UserNode:${userRedux?.id}`,
        skillCodes: [skillCode || ''],
        actionTypeId: ['ActionTypeNode:3', 'ActionTypeNode:4', 'ActionTypeNode:5'],
      },
      skip: !!targetGroupRedux?.id,
    },
    { fetchPolicy: 'network-only', fetchKey: forceRefetch },
  );

  const [skillsPresignedURLsQueryReference, loadSkillsPresignedURLsQuery] =
    useQueryLoader<SkillsPresignedURLsQuery>(getPresignedURLsForSkill);

  // Read here only for the step the page opens on: a skill won on exit tickets opens on the Skill
  // Challenge, where the screen that says so lives. The chip and the message are read further down,
  // in SkillResourceHeader.
  const { inferredViaExitSlip } = useSkillExitSlipState(
    skillCode,
    skillStatesByStudentId[0] as unknown as StudentSkillStates,
  );

  // A prior Skill Challenge is stale when a DG reset is pending, or when it was taken before
  // the latest completed diagnostic (the reset does not clear SK data for skills passed via SK).
  const isAssessmentStale = (sessionCompletedOn?: string | null): boolean => {
    if (latestCompletedStudentSkillDiagnostic?.retakeAssigned) return true;
    const diagnosticCompletedOn = latestCompletedStudentSkillDiagnostic?.sessionCompletedOn;
    if (!diagnosticCompletedOn || !sessionCompletedOn) return false;
    return new Date(sessionCompletedOn).getTime() < new Date(diagnosticCompletedOn).getTime();
  };

  const hasFreshAssessment = (skillAssessmentsByStudentId?.nodes ?? []).some(
    el => !isAssessmentStale(el.sessionCompletedOn),
  );

  const challengeInProgress = assessmentInProgress && !inferredViaExitSlip;

  useEffect(() => {
    if (currentTargetGroupByStudentId) {
      dispatch(setTargetGroup(currentTargetGroupByStudentId.targetGroup as TargetGroup));
    }
  }, [currentTargetGroupByStudentId]);

  useEffect(() => {
    const sSS = skillStatesByStudentId[0];
    if (sSS?.skillSubstate?.name) {
      let skillSubstate;
      let score;
      let maxScore;

      if (
        sSAfterTakeAssessmentRedux.previousState?.id === sSS.id &&
        sSAfterTakeAssessmentRedux.previousState?.skillSubstate.id === sSS.skillSubstate.id &&
        sSAfterTakeAssessmentRedux.previousState?.skillAssessment?.id === sSS.skillAssessment?.id &&
        sSAfterTakeAssessmentRedux.nextState
      ) {
        skillSubstate = inferredOrDiscrepancy(sSAfterTakeAssessmentRedux.nextState?.skillSubstate.id as string)
          ? SkillSubStates.NOT_TESTED
          : (sSAfterTakeAssessmentRedux.nextState?.skillSubstate.id as string);
        score = sSAfterTakeAssessmentRedux.nextState?.skillAssessment?.score;
        maxScore = sSAfterTakeAssessmentRedux.nextState?.skillAssessment?.maxScore;
      } else {
        skillSubstate = sSS?.skillSubstate?.id;
        score = sSS?.skillAssessment?.score;
        maxScore = sSS?.skillAssessment?.maxScore;
      }

      const { className, status } = mapSkillStatus(skillSubstate, score, maxScore);
      setCurrentStatus({ classname: className, status });
    }

    setSSSAfterTakeAssessment({
      previousState: sSS as StudentSkillStates,
      nextState:
        sSAfterTakeAssessmentRedux.nextState && sSAfterTakeAssessmentRedux.nextState.id === sSS.id
          ? sSAfterTakeAssessmentRedux.nextState
          : null,
    });
  }, [skillStatesByStudentId[0]]);

  useEffect(() => {
    if (skillAssessmentsByStudentId?.nodes) {
      setPreviousAttempts((skillAssessmentsByStudentId.nodes as unknown as PreviousAttempts[]) || []);

      setPreviousStatus(
        skillAssessmentsByStudentId.nodes
          .map(el => {
            return {
              id: el.id,
              date: el.sessionCompletedOn,
              score: el.score,
              maxScore: el.maxScore,
            };
          })
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
      );
    }
  }, [skillAssessmentsByStudentId]);

  // Restoring the student's last position is page-load initialization, not a continuous
  // derivation, so it must run at most once per skill. `allUserActions` is a fresh object on every
  // query resolution, and entering the Skill Challenge deliberately logs no UserAction (see the
  // `option !== SKILL_CHALLENGE` branch in TimeLineWrapper.onClickHandler). So re-running this
  // replays the *previous* step and rewinds a student out of an in-flight attempt, while
  // `assessmentInProgress` stays true — hiding the breadcrumb, header and timeline around
  // About-the-Skill content. A refetch is not hypothetical: any Relay store reset does it.
  const restoredStepForSkill = useRef<string | null>(null);

  useEffect(() => {
    const skill = skillCode ?? '';
    if (restoredStepForSkill.current === skill) return;
    restoredStepForSkill.current = skill;

    // A `?step=` link is an explicit request, so it outranks the step restored from the student's last
    // recorded action below — that restoration is a convenience for returning to the page, not a
    // decision. It also bypasses the Skill Challenge review-mode guard: the link asks for the challenge
    // itself, not for a look back at the last attempt.
    if (requestedStep !== null) {
      setCurrentStep(requestedStep);
      // A skill demonstrated on exit tickets is the exception: that step shows what earned it, with no
      // Learnosity session behind it.
      if (requestedStep === SkillResourceSteps.SKILL_CHALLENGE && !inferredViaExitSlip) {
        setSkipNewActivityId(false);
        if (!cooldownCtx?.isBlocked(skill)) {
          setAssessmentInProgress(true);
        }
        setSearchParams(
          params => {
            params.delete(SKILL_RESOURCE_STEP_PARAM);
            return params;
          },
          { replace: true },
        );
      }
      return;
    }
    // No explicit step asked for, and the skill is already the student's through exit tickets: the
    // design lands them on the Skill Challenge to be told so, rather than on About the Skill. This
    // outranks the step restored from their last recorded action for the same reason the deep link
    // does — there is nothing left to do here, and the result is the point.
    //
    // Deliberately decided off the substate the page query already carries, not off the optimistic
    // reading the exit-ticket report adds a moment later: a student reading About the Skill must not
    // be moved off it mid-sentence when a second request lands. The freshly-earned case arrives from
    // the dashboard's result card, which already carries `?step=skill-challenge`.
    if (inferredViaExitSlip) {
      setCurrentStep(SkillResourceSteps.SKILL_CHALLENGE);
      return;
    }
    if (allUserActions?.nodes?.length) {
      if (allUserActions.nodes[0].description === 'Practice Problems')
        setCurrentStep(SkillResourceSteps.PRACTICE_PROBLEMS);
      if (allUserActions.nodes[0].description === 'Worked Example Video')
        setCurrentStep(SkillResourceSteps.WORKED_EXAMPLE);
      if (allUserActions.nodes[0].description === 'About the skill') setCurrentStep(SkillResourceSteps.ABOUT_THE_SKILL);
      if (allUserActions.nodes[0].description === 'Skill Challenge') {
        // Only reopen in review mode if the latest Skill Challenge is still valid, i.e. it was
        // taken after the latest diagnostic. Pre-reset attempts fall back to About the Skill.
        if (skillStatesByStudentId[0]?.skillAssessment && hasFreshAssessment) {
          //open in a review mode
          setCurrentStep(SkillResourceSteps.SKILL_CHALLENGE);
        } else {
          setCurrentStep(SkillResourceSteps.ABOUT_THE_SKILL);
        }
      }
    } else {
      setCurrentStep(SkillResourceSteps.ABOUT_THE_SKILL);
    }
  }, [allUserActions, hasFreshAssessment, skillCode, requestedStep]);

  useEffect(() => {
    loadSkillsPresignedURLsQuery({
      skillCode: skillCode || '',
      filter: { storedObjectType: 'WORKED_EXAMPLE_VIDEO' },
    });
  }, [skillCode]);

  const breadcrumbItems = [
    {
      label: skillStatesByStudentId[0]?.skill?.name || '',
      command: () => setCurrentStep(0),
      template: () => (
        <span className="tw-font-atkinson tw-text-sm tw-text-stone-900 tw-font-bold">
          {skillStatesByStudentId[0]?.skill?.name || ''}
        </span>
      ),
    },
  ];

  const home = {
    template: () => (
      <Link
        to={'/'}
        className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-font-atkinson tw-text-neutral-700"
      >
        <House className="tw-text-blue-500" /> My top picks
      </Link>
    ),
  };

  return (
    <Suspense fallback={<ThreeDotLoader dark />}>
      <div
        style={{ overflowX: 'hidden' }}
        className={`nc_page-inner xl:tw-w-10/12 tw-mx-auto tw-w-11/12 ${
          challengeInProgress ? 'paper-grid-bg nc_skill-challenge-bg' : ''
        }`}
      >
        {!challengeInProgress && (
          <BreadCrumb model={breadcrumbItems} home={home} className="col-12 col-offset-0 tw-py-0" />
        )}
        {/* Renders nothing: it puts this student's exit-ticket report in redux for the header's chip
            and message to read. Mounted at the page level so it survives the step changes below. */}
        <SkillExitSlipReportQuery />
        {skillStatesByStudentId && targetGroupRedux?.id && skillsPresignedURLsQueryReference && (
          <SkillResource
            setAttemptInformation={setAttemptInformation}
            attemptInformation={attemptInformation}
            setPreviousAttempts={setPreviousAttempts}
            previousAttempts={previousAttempts}
            studentStateInfo={skillStatesByStudentId}
            setCurrentStatus={setCurrentStatus}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            sssAfterTakeAssessment={sssAfterTakeAssessment as PreviousAndNextSSS}
            skipNewActivityId={skipNewActivityId}
            setSkipNewActivityId={setSkipNewActivityId}
            skillsPresignedURLsQueryReference={
              skillsPresignedURLsQueryReference as PreloadedQuery<SkillsPresignedURLsQuery, Record<string, unknown>>
            }
            previousStatus={previousStatus}
            setPreviousStatus={setPreviousStatus}
            currentStatus={currentStatus}
            allUserActions={allUserActions}
            assessmentInProgress={challengeInProgress}
            setAssessmentInProgress={setAssessmentInProgress}
          />
        )}
      </div>
    </Suspense>
  );
};
